/**
 * `person` — monday's deprecated single-assignee column, aliased to `item_assignees`
 * (M2 §5.7). Same picker, limited to one; `text` is empty even when populated `[M2§7]`.
 */
import type { PersonValue } from '@elizmonday/shared';
import { AvatarStack, PeoplePicker, nameResolver } from '../people/index';
import { useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { PersonSettingsDialog } from './SettingsDialog';

export const personColumnUi = defineColumnUi<PersonValue, Record<string, unknown>>({
  type: 'person',
  icon: 'columnPeople',
  gallery: { group: 'people', keywords: ['person', 'reviewer', 'single assignee'] },
  /** P-022: `90397660` `Owner` (`width: null`) renders **140** px in monday. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,

  Cell: ({ value, context }) => <AvatarStack ids={value ? [value.id] : []} context={context} limit={1} />,

  /** **P-282**: the picked person follows the store while untouched; an untouched dismiss
   * writes nothing (`useEditorDraft`). */
  Editor: ({ value, context, onCommit, onCancel }) => {
    const editor = useEditorDraft<number[]>(value ? [value.id] : []);
    const picked = editor.draft;
    return (
      <PeoplePicker
        people={context.people}
        picked={picked}
        max={1}
        onToggle={(id) => editor.update((current) => (current.includes(id) ? [] : [id]))}
        onDismiss={() =>
          editor.dismiss(
            () => onCommit(picked.length === 0 ? null : ({ id: picked[0]! } as PersonValue)),
            onCancel,
          )
        }
        onCancel={onCancel}
      />
    );
  },

  SettingsDialog: PersonSettingsDialog,

  fromClipboard(text, _settings, context) {
    const name = text.trim();
    if (name === '') return null;
    const person = context.people.find((entry) => entry.name.toLowerCase() === name.toLowerCase());
    if (!person) throw new Error(`"${name}" is not a member of this board`);
    return { id: Number(person.id) } as PersonValue;
  },
  /**
   * **P-126.** *Not* the API `text`: `person`'s `toText` is `""` on purpose, to byte-match
   * monday's `column_values.text`, which is empty for 74 of the 99 sampled cells `[cvs§2
   * person]` (q67). monday's **grid** copies the assignee's name, which is exactly what
   * `fromClipboard` above reads back, so the clipboard resolves the name from the context and
   * leaves `/v2`'s empty `text` alone.
   */
  toClipboard(value, _settings, context) {
    if (value === null) return '';
    return nameResolver(context)({ id: value.id }) ?? '';
  },
  bucketsOf: sharedBuckets('person'),

  /** A card's cover band: the assignee's photo `[V2§7.5]`. */
  coverImageOf(value, _settings, context) {
    if (value === null) return null;
    return context.people.find((entry) => String(entry.id) === String(value.id))?.photoUrl ?? null;
  },
});
